/**
 * Auth API service.
 * Pure async functions — no React state, no hooks.
 *
 * The refresh token is an httpOnly cookie set by the backend; these calls rely
 * on the axios client sending credentials. Only the short-lived access token is
 * returned in the body.
 */
import { post, get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type {
  AuthUser,
  LoginCredentials,
  LoginResponse,
  LoginResult,
  MfaConfirmResponse,
  MfaSetupResponse,
} from '@features/auth/types';
import { isMfaRequired, normalizeAuthUser } from '@features/auth/types';

async function withNormalizedUser(
  promise: Promise<LoginResponse>,
): Promise<LoginResponse> {
  const res = await promise;
  return { ...res, user: normalizeAuthUser(res.user) };
}

export const authService = {
  /** Authenticate with email + password. May return mfa_required. */
  async login(payload: LoginCredentials): Promise<LoginResult> {
    const raw = await post<LoginResult>(endpoints.auth.login, payload);
    if (isMfaRequired(raw)) return raw;
    return {
      ...raw,
      user: normalizeAuthUser(raw.user),
    };
  },

  /** Complete MFA challenge after password login. */
  verifyMfa(payload: {
    mfaToken: string;
    code?: string;
    recoveryCode?: string;
  }): Promise<LoginResponse> {
    return withNormalizedUser(post(endpoints.auth.mfaVerify, payload));
  },

  mfaSetup(): Promise<MfaSetupResponse> {
    return post(endpoints.auth.mfaSetup);
  },

  async mfaConfirm(code: string): Promise<MfaConfirmResponse> {
    const res = await post<MfaConfirmResponse>(endpoints.auth.mfaConfirm, {
      code,
    });
    return {
      ...res,
      user: normalizeAuthUser(res.user),
      recoveryCodes: res.recoveryCodes,
    };
  },

  mfaDisable(payload: { password: string; code: string }): Promise<void> {
    return post(endpoints.auth.mfaDisable, payload);
  },

  regenerateRecoveryCodes(payload: {
    password: string;
    code: string;
  }): Promise<{ recoveryCodes: string[] }> {
    return post(endpoints.auth.mfaRegenerate, payload);
  },

  /** Exchange the refresh cookie for a new access token (rotates the cookie). */
  refresh(): Promise<LoginResponse> {
    return withNormalizedUser(post(endpoints.auth.refresh));
  },

  /** Invalidate the current session (revokes + clears the refresh cookie). */
  logout(): Promise<void> {
    return post(endpoints.auth.logout);
  },

  /** Fetch the currently authenticated session (permissions, memberships, …). */
  async me(): Promise<AuthUser> {
    const raw = await get<AuthUser>(endpoints.auth.me);
    return normalizeAuthUser(raw);
  },

  /**
   * Persist active tenant and return an updated session.
   * Clears stale client tenant/agent state in the auth store caller.
   */
  async setActiveTenant(tenantId: string): Promise<AuthUser> {
    const raw = await post<AuthUser>(endpoints.auth.activeTenant, { tenantId });
    return normalizeAuthUser(raw);
  },

  /** Always returns a generic message (no email enumeration). */
  forgotPassword(email: string): Promise<{ message: string }> {
    return post(endpoints.auth.forgotPassword, { email });
  },

  resetPassword(token: string, password: string): Promise<void> {
    return post(endpoints.auth.resetPassword, { token, password });
  },
} as const;
